import React from 'react';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';

/**
 * Single source of truth for how VMS invoice/batch statuses are presented.
 *
 * Invoice statuses: 'pre' | 'Success' | 'Failed' | 'ValidationFailed' | 'NotProcessed'
 * Batch statuses:   'Processing' | 'Pending' | 'Completed' | 'Failed' | 'ValidationFailed' | 'Error'
 *
 * 'Partial Success' is not returned by the API - it is derived on the client by
 * `getBatchDisplayStatus`.
 */
const STATUS_PRESENTATION = {
  pre: { label: 'Pre', color: 'orange', icon: <ClockCircleOutlined /> },
  pending: { label: 'Pending', color: 'orange', icon: <ClockCircleOutlined /> },
  processing: { label: 'Processing', color: 'blue', icon: <ClockCircleOutlined spin /> },
  success: { label: 'Success', color: 'green', icon: <CheckCircleOutlined /> },
  completed: { label: 'Completed', color: 'green', icon: <CheckCircleOutlined /> },
  'partial success': { label: 'Partial Success', color: 'orange', icon: <ExclamationCircleOutlined /> },
  failed: { label: 'Failed', color: 'red', icon: <CloseCircleOutlined /> },
  validationfailed: { label: 'Validation Failed', color: 'red', icon: <ExclamationCircleOutlined /> },
  error: { label: 'Error', color: 'red', icon: <ExclamationCircleOutlined /> },
  notprocessed: { label: 'Not Processed', color: 'default', icon: <MinusCircleOutlined /> }
};

const normalise = (status) => (status ?? '').toString().trim().toLowerCase();

/**
 * Human-readable label for a status.
 *
 * The list of statuses is open, so an unrecognised value is made readable
 * ("SomethingNew" -> "Something New") rather than rendered raw or dropped.
 *
 * @param {string} status
 * @returns {string}
 */
export const getStatusLabel = (status) => {
  if (!status) return 'Unknown';

  const known = STATUS_PRESENTATION[normalise(status)];
  if (known) return known.label;

  return String(status)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (char) => char.toUpperCase());
};

/**
 * antd Tag colour for a status. Unknown statuses stay neutral.
 *
 * @param {string} status
 * @returns {string}
 */
export const getStatusColor = (status) => STATUS_PRESENTATION[normalise(status)]?.color ?? 'default';

/**
 * antd Tag icon for a status. Unknown statuses get a neutral icon rather than
 * an error or success one.
 *
 * @param {string} status
 * @returns {JSX.Element}
 */
export const getStatusIcon = (status) =>
  STATUS_PRESENTATION[normalise(status)]?.icon ?? <MinusCircleOutlined />;

/**
 * True when VMS never evaluated the invoice. Not a failure.
 *
 * @param {string} status
 * @returns {boolean}
 */
export const isNotProcessed = (status) => normalise(status) === 'notprocessed';

const FAILURE_STATUSES = ['failed', 'validationfailed', 'error'];

/**
 * Status to show for a batch.
 *
 * A batch is flagged 'Failed'/'ValidationFailed'/'Error' as soon as anything
 * went wrong, even when part of it uploaded fine.
 *
 * @param {Object} batch - Batch row from /search
 * @returns {string}
 */
export const getBatchDisplayStatus = (batch) => {
  const successful = Number(batch?.successfulUploads);
  const total = Number(batch?.totalInvoices);

  const isPartial =
    FAILURE_STATUSES.includes(normalise(batch?.status)) &&
    Number.isFinite(successful) &&
    Number.isFinite(total) &&
    successful > 0 &&
    successful < total;

  return isPartial ? 'Partial Success' : batch?.status;
};
